import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowBackIosNew,
  ChevronRight,
  FilterList,
  LockOutlined,
  Numbers,
  PlaceOutlined,
  Search,
  ShieldOutlined,
  VpnKeyOutlined,
} from "@mui/icons-material";
import { useMemo, useState } from "react";
import { Pickup } from "types/pickup";
import { Location } from "types/location";
import { InputBase } from "components/common/Input";
import useDebouncedRemoteFilterSearch from "hooks/useDebouncedRemoteFilterSearch";
import theme from "lib/theme";

type FilterOption = { label: string; value: string };
type SubmenuKey = "needsAttention" | "enrolledStatus" | "securityLocks" | "pickup" | "location";

type AssetsFiltersMenuProps = {
  needsAttention: boolean;
  onNeedsAttentionChange: (checked: boolean) => void;
  enrolledStatuses: string[];
  onEnrolledStatusesChange: (statuses: string[]) => void;
  enrolledStatusOptions: FilterOption[];
  securityLevels: string[];
  onSecurityLevelsChange: (levels: string[]) => void;
  securityLocksOptions: FilterOption[];
  pickupIds: string[];
  onPickupIdsChange: (ids: string[]) => void;
  pickups: Pickup[];
  locationIds: string[];
  onLocationIdsChange: (ids: string[]) => void;
  locations: Location[];
  onClearAll: () => void;
  pickupListItemRef?: (node: HTMLElement | null) => void;
  enrollmentV2Enabled?: boolean;
  onCatalogNeeded?: (catalog: "pickup" | "location") => void;
  pickupsLoading?: boolean;
  locationsLoading?: boolean;
};

const SUBMENU_TITLES: Record<SubmenuKey, string> = {
  needsAttention: "NEEDS ATTENTION",
  enrolledStatus: "ENROLLED STATUS",
  securityLocks: "SECURITY LOCKS",
  pickup: "PICKUP ID",
  location: "LOCATION",
};

const SUBMENU_SEARCH_PLACEHOLDERS: Record<SubmenuKey, string> = {
  needsAttention: "Search...",
  enrolledStatus: "Search enrolled status...",
  securityLocks: "Search security locks...",
  pickup: "Search pickup id...",
  location: "Search location...",
};

const menuItemSx = { py: 1.5, px: 2, gap: 1.5, minWidth: 280 };
const optionItemSx = { py: 0, px: 2, gap: 1.5, alignItems: "center" };

function toggleValue(values: string[], value: string) {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

function selectionSecondary(selectedCount: number, singleLabel?: string | number) {
  if (selectedCount === 0) return undefined;
  if (selectedCount === 1) return singleLabel;
  return `${selectedCount} selected`;
}

function filterByLabel<T extends { label: string }>(options: T[], searchQuery: string) {
  if (!searchQuery) return options;
  return options.filter(({ label }) => label.toLowerCase().includes(searchQuery));
}

export default function AssetsFiltersMenu({
  needsAttention,
  onNeedsAttentionChange,
  enrolledStatuses,
  onEnrolledStatusesChange,
  enrolledStatusOptions,
  securityLevels,
  onSecurityLevelsChange,
  securityLocksOptions,
  pickupIds,
  onPickupIdsChange,
  pickups,
  locationIds,
  onLocationIdsChange,
  locations,
  onClearAll,
  pickupListItemRef,
  enrollmentV2Enabled,
  onCatalogNeeded,
  pickupsLoading = false,
  locationsLoading = false,
}: AssetsFiltersMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [submenu, setSubmenu] = useState<SubmenuKey | null>(null);
  const [submenuSearch, setSubmenuSearch] = useState("");

  const pickupSearch = useDebouncedRemoteFilterSearch<Pickup>({
    query: submenuSearch,
    enabled: submenu === "pickup",
    cancelKey: "assets-filters-pickup-search",
    path: "/pickups",
    filterColumn: "order_number",
    responseKey: "pickups",
    pageSize: 25,
  });

  const locationSearch = useDebouncedRemoteFilterSearch<Location>({
    query: submenuSearch,
    enabled: submenu === "location",
    cancelKey: "assets-filters-location-search",
    path: "/locations",
    filterColumn: "name",
    responseKey: "locations",
  });

  const open = Boolean(anchorEl);
  const searchQuery = submenuSearch.trim().toLowerCase();
  const remoteSearchPending = pickupSearch.isPending || locationSearch.isPending;
  const showMinCharsHint =
    (submenu === "pickup" || submenu === "location") &&
    submenuSearch.trim().length > 0 &&
    submenuSearch.trim().length < pickupSearch.minChars;

  const activeFilterCount = useMemo(() => {
    return (
      Number(needsAttention) +
      Number(enrolledStatuses.length > 0) +
      Number(securityLevels.length > 0) +
      Number(pickupIds.length > 0) +
      Number(locationIds.length > 0)
    );
  }, [needsAttention, enrolledStatuses, securityLevels, pickupIds, locationIds]);

  const needsAttentionOptions = useMemo(
    () => filterByLabel([{ label: "Needs Attention", value: true }], searchQuery),
    [searchQuery]
  );
  const filteredEnrolledStatusOptions = useMemo(
    () => filterByLabel(enrolledStatusOptions, searchQuery),
    [enrolledStatusOptions, searchQuery]
  );
  const filteredSecurityLocksOptions = useMemo(
    () => filterByLabel(securityLocksOptions, searchQuery),
    [securityLocksOptions, searchQuery]
  );

  const filteredPickups = useMemo(() => {
    if (showMinCharsHint && submenu === "pickup") return [];
    const source = pickupSearch.isRemoteSearch ? pickupSearch.results : pickups;
    return enrollmentV2Enabled ? source : source.filter(({ scheduledAt }) => scheduledAt);
  }, [pickups, pickupSearch.isRemoteSearch, pickupSearch.results, enrollmentV2Enabled, showMinCharsHint, submenu]);

  const filteredLocations = useMemo(() => {
    if (showMinCharsHint && submenu === "location") return [];
    const source = locationSearch.isRemoteSearch ? locationSearch.results : locations;
    return source.filter(({ name }) => Boolean(name?.trim()));
  }, [locations, locationSearch.isRemoteSearch, locationSearch.results, showMinCharsHint, submenu]);

  const pickupCatalogLoading = pickupsLoading && submenu === "pickup" && !pickupSearch.isRemoteSearch;
  const locationCatalogLoading = locationsLoading && submenu === "location" && !locationSearch.isRemoteSearch;
  const catalogLoading = pickupCatalogLoading || locationCatalogLoading;
  const showPickupSpinner =
    submenu === "pickup" &&
    (pickupSearch.isPending || (pickupCatalogLoading && filteredPickups.length === 0 && !showMinCharsHint));
  const showLocationSpinner =
    submenu === "location" &&
    (locationSearch.isPending || (locationCatalogLoading && filteredLocations.length === 0 && !showMinCharsHint));

  const hasNoMatches =
    !remoteSearchPending &&
    !catalogLoading &&
    ((submenu === "needsAttention" && needsAttentionOptions.length === 0) ||
      (submenu === "enrolledStatus" && filteredEnrolledStatusOptions.length === 0) ||
      (submenu === "securityLocks" && filteredSecurityLocksOptions.length === 0) ||
      (submenu === "pickup" && filteredPickups.length === 0) ||
      (submenu === "location" && filteredLocations.length === 0));

  const resetMenuState = () => {
    pickupSearch.reset();
    locationSearch.reset();
    setSubmenu(null);
    setSubmenuSearch("");
  };

  const handleClose = () => {
    resetMenuState();
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<FilterList />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          whiteSpace: "nowrap",
          borderStyle: "dashed",
          borderColor: theme.palette.text.disabled,
          color: theme.palette.text.primary,
          bgcolor: "#fff",
          height: 37,
          px: 2,
          flexShrink: 0,
          "&:hover": {
            borderColor: theme.palette.text.primary,
            bgcolor: "#fff",
          },
        }}
      >
        Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            minWidth: 300,
            overflow: "hidden",
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        {submenu == null ? (
          <>
            <Box sx={{ py: 1 }}>
              <MenuItem
                sx={menuItemSx}
                onClick={() => {
                  resetMenuState();
                  setSubmenu("needsAttention");
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <LockOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Needs Attention"
                  secondary={needsAttention ? "On" : undefined}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
              </MenuItem>

              <MenuItem
                sx={menuItemSx}
                onClick={() => {
                  resetMenuState();
                  setSubmenu("enrolledStatus");
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ShieldOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Enrolled Status"
                  secondary={selectionSecondary(
                    enrolledStatuses.length,
                    enrolledStatusOptions.find(({ value }) => value === enrolledStatuses[0])?.label ||
                      enrolledStatuses[0]
                  )}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
              </MenuItem>

              <MenuItem
                sx={menuItemSx}
                onClick={() => {
                  resetMenuState();
                  setSubmenu("securityLocks");
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <VpnKeyOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Security Locks"
                  secondary={selectionSecondary(
                    securityLevels.length,
                    securityLocksOptions.find(({ value }) => value === securityLevels[0])?.label || securityLevels[0]
                  )}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
              </MenuItem>

              <MenuItem
                sx={menuItemSx}
                onClick={() => {
                  resetMenuState();
                  onCatalogNeeded?.("pickup");
                  setSubmenu("pickup");
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Numbers fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Pickup ID"
                  secondary={selectionSecondary(
                    pickupIds.length,
                    pickups.find(({ id }) => id.toString() === pickupIds[0])?.orderNumber
                  )}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
              </MenuItem>

              <MenuItem
                sx={menuItemSx}
                onClick={() => {
                  resetMenuState();
                  onCatalogNeeded?.("location");
                  setSubmenu("location");
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <PlaceOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Location"
                  secondary={selectionSecondary(
                    locationIds.length,
                    locations.find(({ id }) => id?.toString() === locationIds[0])?.name
                  )}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
              </MenuItem>
            </Box>

            {activeFilterCount > 0 && (
              <>
                <Divider />
                <MenuItem
                  sx={{ ...menuItemSx, color: theme.palette.primary.main, fontWeight: 600 }}
                  onClick={() => {
                    onClearAll();
                    handleClose();
                  }}
                >
                  Clear all filters
                </MenuItem>
              </>
            )}
          </>
        ) : (
          <>
            <Stack direction="row" alignItems="center" gap={0.5} px={1} py={1}>
              <IconButton aria-label="Back to filters" size="small" onClick={resetMenuState}>
                <ArrowBackIosNew sx={{ fontSize: 14 }} />
              </IconButton>
              <Typography
                variant="caption"
                fontWeight={700}
                letterSpacing="0.06em"
                color="text.secondary"
                sx={{ textTransform: "uppercase" }}
              >
                {SUBMENU_TITLES[submenu]}
              </Typography>
            </Stack>

            <Divider />

            <Box px={2} py={0.75}>
              <InputBase
                size="small"
                placeholder={SUBMENU_SEARCH_PLACEHOLDERS[submenu]}
                value={submenuSearch}
                onChange={(e) => setSubmenuSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ".MuiInputBase-input": {
                    fontSize: 14,
                    padding: "0 !important",
                  },
                  fieldset: {
                    border: "none",
                  },
                }}
              />
            </Box>

            <Divider />

            <Box sx={{ maxHeight: 280, overflowY: "auto", py: 0.5 }}>
              {submenu === "needsAttention" &&
                needsAttentionOptions.map(({ label, value }) => (
                  <MenuItem
                    key={label}
                    sx={optionItemSx}
                    selected={needsAttention === value}
                    onClick={() => onNeedsAttentionChange(!needsAttention)}
                  >
                    <Checkbox edge="start" checked={needsAttention} size="small" tabIndex={-1} disableRipple />
                    <ListItemText primary={label} />
                  </MenuItem>
                ))}

              {submenu === "enrolledStatus" &&
                filteredEnrolledStatusOptions.map(({ label, value }) => {
                  const selected = enrolledStatuses.includes(value);
                  return (
                    <MenuItem
                      key={value}
                      sx={optionItemSx}
                      selected={selected}
                      onClick={() => onEnrolledStatusesChange(toggleValue(enrolledStatuses, value))}
                    >
                      <Checkbox edge="start" checked={selected} size="small" tabIndex={-1} disableRipple />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}

              {submenu === "securityLocks" &&
                filteredSecurityLocksOptions.map(({ label, value }) => {
                  const selected = securityLevels.includes(value);
                  return (
                    <MenuItem
                      key={value}
                      sx={optionItemSx}
                      selected={selected}
                      onClick={() => onSecurityLevelsChange(toggleValue(securityLevels, value))}
                    >
                      <Checkbox edge="start" checked={selected} size="small" tabIndex={-1} disableRipple />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}

              {showPickupSpinner && (
                <Box px={2} py={2} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              )}

              {submenu === "pickup" &&
                !pickupSearch.isPending &&
                !(pickupCatalogLoading && filteredPickups.length === 0) &&
                filteredPickups.map((pickup) => {
                  const id = pickup.id.toString();
                  const selected = pickupIds.includes(id);
                  return (
                    <MenuItem
                      key={pickup.id}
                      ref={pickupSearch.isRemoteSearch ? undefined : pickupListItemRef}
                      sx={optionItemSx}
                      selected={selected}
                      onClick={() => onPickupIdsChange(toggleValue(pickupIds, id))}
                    >
                      <Checkbox edge="start" checked={selected} size="small" tabIndex={-1} disableRipple />
                      <ListItemText primary={String(pickup.orderNumber).toUpperCase()} />
                      {pickup.inventoryCount != null && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, flexShrink: 0 }}>
                          {pickup.inventoryCount} {pickup.inventoryCount === 1 ? "asset" : "assets"}
                        </Typography>
                      )}
                    </MenuItem>
                  );
                })}

              {showLocationSpinner && (
                <Box px={2} py={2} display="flex" justifyContent="center">
                  <CircularProgress size={20} />
                </Box>
              )}

              {submenu === "location" &&
                !locationSearch.isPending &&
                !(locationCatalogLoading && filteredLocations.length === 0) &&
                filteredLocations.map((location) => {
                  const id = location.id?.toString() ?? "";
                  const selected = locationIds.includes(id);
                  return (
                    <MenuItem
                      key={location.id}
                      sx={optionItemSx}
                      selected={selected}
                      onClick={() => onLocationIdsChange(toggleValue(locationIds, id))}
                    >
                      <Checkbox edge="start" checked={selected} size="small" tabIndex={-1} disableRipple />
                      <ListItemText primary={location.name} />
                    </MenuItem>
                  );
                })}

              {showMinCharsHint && !remoteSearchPending && (
                <Box px={2} py={2}>
                  <Typography variant="body2" color="text.secondary">
                    Enter at least {pickupSearch.minChars} characters to search
                  </Typography>
                </Box>
              )}

              {hasNoMatches && !showMinCharsHint && (
                <Box px={2} py={2}>
                  <Typography variant="body2" color="text.secondary">
                    No matches
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Menu>
    </>
  );
}
